/**
 * Descriptors dos campos de formulário.
 *
 * Cada um declara: como aparece na paleta, o estado inicial, onde pode ser solto,
 * as propriedades específicas do inspector e como participa do formulário.
 * O que é comum a todo campo vem de `common-groups`.
 */
import { ScreenNode } from '../../model/node-props';
import { ComponentDescriptor, defineDescriptor } from '../component-descriptor';
import { buildDependencyGroup, buildStandardFieldGroups } from './common-groups';

/** Todo campo pode ficar em painel, linha ou seção. */
/**
 * Onde um campo pode morar.
 *
 * `tab` entrou depois do relato "esta exigindo que sempre tenha um painel":
 * uma aba com um campo so nao precisa de painel nenhum, e obrigar a criar um
 * antes e a mesma friccao que fazia toda tela sair parecendo o mesmo cadastro.
 * O painel continua sendo o normal quando ha muitos campos.
 */
const FIELD_PARENTS = ['panel', 'row', 'section', 'tab'] as const;

// ---------------------------------------------------------------- texto

export const textDescriptor = defineDescriptor<'text'>({
  type: 'text',
  labelKey: 'builderScreen.palette.text',
  descriptionKey: 'builderScreen.palette.text.desc',
  icon: 'font-size',
  category: 'input',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '' },
    layout: { order: context.order, colSpan: 8 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'text',
      path: 'defaultValue',
      labelKey: 'builderScreen.inspector.defaultValue',
      helpKey: 'builderScreen.inspector.defaultValue.help',
      level: 'simple'
    },
    {
      kind: 'number',
      path: 'minLength',
      labelKey: 'builderScreen.inspector.minLength',
      helpKey: 'builderScreen.inspector.minLength.help',
      level: 'advanced',
      min: 0
    },
    {
      kind: 'number',
      path: 'maxLength',
      labelKey: 'builderScreen.inspector.maxLength',
      helpKey: 'builderScreen.inspector.maxLength.help',
      level: 'advanced',
      min: 0
    },
    {
      kind: 'text',
      path: 'pattern',
      labelKey: 'builderScreen.inspector.pattern',
      level: 'advanced'
    }
  ]),

  form: {
    defaultValue: node => (node.props as { defaultValue?: string }).defaultValue ?? '',
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

export const textareaDescriptor = defineDescriptor<'textarea'>({
  type: 'textarea',
  labelKey: 'builderScreen.palette.textarea',
  descriptionKey: 'builderScreen.palette.textarea.desc',
  icon: 'align-left',
  category: 'input',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', minRows: 3 },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'multiline',
      path: 'defaultValue',
      labelKey: 'builderScreen.inspector.defaultValue',
      level: 'simple',
      rows: 2
    },
    {
      kind: 'number',
      path: 'minRows',
      labelKey: 'builderScreen.inspector.minRows',
      helpKey: 'builderScreen.inspector.minRows.help',
      level: 'simple',
      min: 1,
      max: 20
    },
    {
      kind: 'number',
      path: 'maxLength',
      labelKey: 'builderScreen.inspector.maxLength',
      level: 'advanced',
      min: 0
    }
  ]),

  form: {
    defaultValue: node => (node.props as { defaultValue?: string }).defaultValue ?? '',
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

// ---------------------------------------------------------------- número

export const numberDescriptor = defineDescriptor<'number'>({
  type: 'number',
  labelKey: 'builderScreen.palette.number',
  descriptionKey: 'builderScreen.palette.number.desc',
  icon: 'number',
  category: 'input',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', decimalPlaces: 2, decimalSeparator: ',', thousandSeparator: '.' },
    layout: { order: context.order, colSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    ...buildStandardFieldGroups([
      {
        kind: 'number',
        path: 'defaultValue',
        labelKey: 'builderScreen.inspector.defaultValue',
        level: 'simple'
      },
      {
        kind: 'number',
        path: 'min',
        labelKey: 'builderScreen.inspector.min',
        helpKey: 'builderScreen.inspector.min.help',
        level: 'simple'
      },
      {
        kind: 'number',
        path: 'max',
        labelKey: 'builderScreen.inspector.max',
        helpKey: 'builderScreen.inspector.max.help',
        level: 'simple'
      }
    ]),
    {
      labelKey: 'builderScreen.inspector.numberFormatTitle',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'custom',
          path: 'decimalPlaces',
          editor: 'numberFormat',
          labelKey: 'builderScreen.inspector.numberFormatTitle',
          level: 'advanced'
        }
      ]
    }
  ],

  form: {
    defaultValue: node => (node.props as { defaultValue?: number }).defaultValue ?? null,
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

// ---------------------------------------------------------------- data

export const dateDescriptor = defineDescriptor<'date'>({
  type: 'date',
  labelKey: 'builderScreen.palette.date',
  descriptionKey: 'builderScreen.palette.date.desc',
  icon: 'calendar',
  category: 'input',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', format: 'dd/MM/yyyy' },
    layout: { order: context.order, colSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'text',
      path: 'format',
      labelKey: 'builderScreen.inspector.dateFormat',
      helpKey: 'builderScreen.inspector.dateFormat.help',
      level: 'simple'
    },
    {
      kind: 'boolean',
      path: 'showTime',
      labelKey: 'builderScreen.inspector.showTime',
      helpKey: 'builderScreen.inspector.showTime.help',
      level: 'simple'
    }
  ]),

  form: {
    defaultValue: node => (node.props as { defaultValue?: string }).defaultValue ?? null,
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

// ---------------------------------------------------------------- sim/não

export const switchDescriptor = defineDescriptor<'switch'>({
  type: 'switch',
  labelKey: 'builderScreen.palette.switch',
  descriptionKey: 'builderScreen.palette.switch.desc',
  icon: 'check',
  category: 'input',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', defaultValue: false },
    layout: { order: context.order, colSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'boolean',
      path: 'defaultValue',
      labelKey: 'builderScreen.inspector.defaultValue',
      level: 'simple'
    },
    {
      kind: 'text',
      path: 'checkedLabelKey',
      labelKey: 'builderScreen.inspector.checkedChildren',
      helpKey: 'builderScreen.inspector.checkedChildren.help',
      level: 'advanced'
    },
    {
      kind: 'text',
      path: 'uncheckedLabelKey',
      labelKey: 'builderScreen.inspector.unCheckedChildren',
      helpKey: 'builderScreen.inspector.unCheckedChildren.help',
      level: 'advanced'
    }
  ]),

  form: {
    defaultValue: node => (node.props as { defaultValue?: boolean }).defaultValue ?? false
  }
});

// ---------------------------------------------------------------- seleção

export const selectDescriptor = defineDescriptor<'select'>({
  type: 'select',
  labelKey: 'builderScreen.palette.select',
  descriptionKey: 'builderScreen.palette.select.desc',
  icon: 'unordered-list',
  category: 'selection',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', options: [] },
    layout: { order: context.order, colSpan: 8 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'custom',
      path: 'options',
      editor: 'selectOptions',
      labelKey: 'builderScreen.inspector.dropdownOptionsTitle',
      level: 'simple'
    },
    {
      kind: 'boolean',
      path: 'multiple',
      labelKey: 'builderScreen.inspector.multiple',
      helpKey: 'builderScreen.inspector.multiple.help',
      level: 'simple'
    },
    {
      kind: 'boolean',
      path: 'showSearch',
      labelKey: 'builderScreen.inspector.showSearch',
      helpKey: 'builderScreen.inspector.showSearch.help',
      level: 'advanced'
    }
  ]),

  form: {
    defaultValue: node => {
      const props = node.props as { multiple?: boolean; defaultValue?: unknown };
      if (props.defaultValue !== undefined) {
        return props.defaultValue;
      }
      return props.multiple === true ? [] : null;
    },
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

/**
 * Lista suspensa. Mantida separada de `select` porque o modelo antigo tratava as
 * duas como tipos distintos e há telas gravadas com cada uma — unificar agora
 * quebraria a migração sem ganho real.
 */
export const dropdownDescriptor = defineDescriptor<'dropdown'>({
  type: 'dropdown',
  labelKey: 'builderScreen.palette.dropdown',
  descriptionKey: 'builderScreen.palette.dropdown.desc',
  icon: 'down',
  category: 'selection',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', options: [] },
    layout: { order: context.order, colSpan: 8 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    ...buildStandardFieldGroups([
      {
        kind: 'custom',
        path: 'options',
        editor: 'selectOptions',
        labelKey: 'builderScreen.inspector.dropdownOptionsTitle',
        level: 'simple'
      },
      {
        kind: 'boolean',
        path: 'multiple',
        labelKey: 'builderScreen.inspector.multiple',
        level: 'simple'
      },
      {
        kind: 'boolean',
        path: 'showSearch',
        labelKey: 'builderScreen.inspector.showSearch',
        level: 'advanced'
      }
    ]),
    buildDependencyGroup()
  ],

  form: {
    defaultValue: node => (node.props as { defaultValue?: unknown }).defaultValue ?? null,
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

/** Opções vindas de consulta. A consulta em si vive em `binding.query`. */
export const querySelectDescriptor = defineDescriptor<'querySelect'>({
  type: 'querySelect',
  labelKey: 'builderScreen.palette.querySelect',
  descriptionKey: 'builderScreen.palette.querySelect.desc',
  icon: 'database',
  category: 'selection',
  level: 'advanced',

  createDefault: context => ({
    props: { labelKey: '' },
    layout: { order: context.order, colSpan: 8 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'multiline',
      path: 'condition',
      target: 'binding',
      labelKey: 'builderScreen.querySelect.properties.condition',
      helpKey: 'builderScreen.querySelect.properties.condition.help',
      level: 'advanced',
      rows: 3
    },
    {
      kind: 'boolean',
      path: 'showKey',
      labelKey: 'builderScreen.inspector.showKey',
      helpKey: 'builderScreen.inspector.showKey.help',
      level: 'advanced'
    }
  ]),

  form: {
    defaultValue: () => null,
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

/** Busca em cadastro do sistema. Entidade e campos em `binding.query`. */
export const searchDescriptor = defineDescriptor<'search'>({
  type: 'search',
  labelKey: 'builderScreen.palette.search',
  descriptionKey: 'builderScreen.palette.search.desc',
  icon: 'search',
  category: 'selection',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: '', showKey: true },
    layout: { order: context.order, colSpan: 8 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    ...buildStandardFieldGroups([
      {
        kind: 'custom',
        path: 'query',
        target: 'binding',
        editor: 'entityQuery',
        labelKey: 'builderScreen.search.properties.entityName',
        helpKey: 'builderScreen.search.properties.entityName.help',
        level: 'simple'
      },
      {
        kind: 'boolean',
        path: 'showKey',
        labelKey: 'builderScreen.search.properties.showKey',
        helpKey: 'builderScreen.search.properties.showKey.label',
        level: 'simple'
      }
    ]),
    buildDependencyGroup()
  ],

  form: {
    defaultValue: () => null,
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

export const tagDescriptor = defineDescriptor<'tag'>({
  type: 'tag',
  labelKey: 'builderScreen.palette.tag',
  descriptionKey: 'builderScreen.palette.tag.desc',
  icon: 'tags',
  category: 'selection',
  level: 'advanced',

  createDefault: context => ({
    props: { labelKey: '', options: [] },
    layout: { order: context.order, colSpan: 12 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'custom',
      path: 'options',
      editor: 'selectOptions',
      labelKey: 'builderScreen.inspector.dropdownOptionsTitle',
      level: 'advanced'
    }
  ]),

  form: {
    defaultValue: () => [],
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

// ---------------------------------------------------------------- chave/valor

export const keyValueDescriptor = defineDescriptor<'keyValue'>({
  type: 'keyValue',
  labelKey: 'builderScreen.palette.keyValue',
  descriptionKey: 'builderScreen.palette.keyValue.desc',
  icon: 'profile',
  category: 'input',
  level: 'advanced',

  createDefault: context => ({
    props: { labelKey: '' },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups(),

  form: {
    defaultValue: () => [],
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

export const keyValueObjectDescriptor = defineDescriptor<'keyValueObject'>({
  type: 'keyValueObject',
  labelKey: 'builderScreen.palette.keyValueObject',
  descriptionKey: 'builderScreen.palette.keyValueObject.desc',
  icon: 'build',
  category: 'input',
  level: 'advanced',

  createDefault: context => ({
    props: { labelKey: '' },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups(),

  form: {
    defaultValue: () => ({}),
    isRequired: node => (node.props as { required?: boolean }).required === true
  }
});

export const FIELD_DESCRIPTORS: readonly ComponentDescriptor[] = [
  textDescriptor,
  textareaDescriptor,
  numberDescriptor,
  dateDescriptor,
  switchDescriptor,
  selectDescriptor,
  dropdownDescriptor,
  querySelectDescriptor,
  searchDescriptor,
  tagDescriptor,
  keyValueDescriptor,
  keyValueObjectDescriptor
];
